import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addHeader, editHeader } from '../../../../api/endpoints/cmsSite';
import type { Header } from '../../../../api/types/cmsSite';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';

const schema = z.object({
  title: z.string().min(1, 'Başlık gerekli'),
  slogan: z.string().optional(),
  facebook: z.string().optional(),
  twitter: z.string().optional(),
  instagram: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { title: '', slogan: '', facebook: '', twitter: '', instagram: '' };

function toFormValues(header: Header): FormValues {
  return {
    title: header.title ?? '',
    slogan: header.slogan ?? '',
    facebook: header.facebook ?? '',
    twitter: header.twitter ?? '',
    instagram: header.instagram ?? '',
  };
}

/** Create/edit dialog for Header. Confirmed via local-backend swagger + live curl (see
 *  api/types/cmsSite.ts header). NOTE: there is no `GetHeaderById` route — edit mode is
 *  seeded entirely from the row object the list page already has in its query cache. Always
 *  rendered inside a specific site's tab, so `site_Id` is injected from the `siteId` prop
 *  rather than user-editable. */
export function HeaderFormDialog({
  open,
  header,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  header: Header | null;
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!header;

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) reset(header ? toFormValues(header) : emptyValues);
  }, [open, header, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && header
        ? editHeader({ id: header.id, site_Id: siteId, ...values })
        : addHeader({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Header güncellendi.' : 'Header oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'headers'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Header'ı Güncelle" : 'Yeni Header'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Başlık"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Slogan" {...register('slogan')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Facebook" {...register('facebook')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Twitter" {...register('twitter')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Instagram" {...register('instagram')} />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
